/**
 * Device table: DataTable for the main list; optional row reorder for house devices.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "react-hot-toast";
import { buildDataTableLabels } from "@/components/table";
import {
  Ban,
  HardDrive,
  Plus,
  KeyRound,
  Link2,
  MoreHorizontal,
  CheckCircle2,
  Check,
  Clock,
  Copy,
  Info,
  Link2Off,
  ArrowLeftRight,
  ArrowUpRight,
  SquarePlus,
  PencilLine,
  StickyNote,
  Users,
  Volume,
  Volume1,
  Volume2,
  Wifi,
  WifiHigh,
  WifiLow,
  MemoryStick,
  type LucideIcon,
} from "lucide-react";
import { CopyableSerialNumber } from "@/components/device/CopyableSerialNumber";
import { ClickToCopyId } from "@/components/common/ClickToCopyId";
import {
  ActionMenu,
  ActionMenuItem,
  Button,
  ConfirmModal,
  HoverPopover,
  Input,
  Modal,
  ModalFooter,
  Switch,
  Textarea,
} from "@/components/ui";
import {
  DataTable,
  createActionsColumn,
  type DataTableColumn,
  type DataTableLoadState,
  type DataTableSelectionBulkAction,
} from "@/components/table";
import {
  DeviceAlertIcons,
  DeviceInUseWarning,
  DeviceSetupBadge,
  DeviceTelemetryDot,
} from "@/components/device/DeviceStatusIndicators";
import { ReaderLiveStatusDot } from "@/components/device/ReaderLiveStatusDot";
import {
  deviceAppVersionSortValue,
  formatDeviceAppVersion,
  getDeviceStatusData,
  getDeviceStatusTimestamp,
  getBatteryIcon,
  readTelemetryBoolean,
  readTelemetryNumber,
  TELEMETRY_FRESH_MS,
  type DeviceStatusPayload,
} from "@/lib/deviceStatus";
import { isReaderPaired } from "@/lib/readerLabel";
import {
  normalizeReaderSerialNumber,
  readerSerialSuffix,
} from "@/lib/readerSerialNumber";
import { cn } from "@/lib/utils";
import {
  formatDate,
  formatDateTime,
  formatRelativeTime,
  todayDateInputValue,
} from "@/lib/datetime";
import type { Device } from "@/types";

export type DeviceTableSortColumn =
  | "device"
  | "serial"
  | "googleAccount"
  | "unlock"
  | "reader"
  | "house"
  | "appVersion"
  | "telemetry"
  | "interaction"
  | "installedOn";

const CELL_TRUNCATE_CLASS = "block min-w-0 truncate";
/** Inline edit affordance: quiet until the cell is hovered or focused. */
const HOVER_REVEAL_CLASS =
  "h-6 w-6 shrink-0 text-surface-400 opacity-0 transition-opacity duration-75 group-hover/cell:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100";
/** Linked entity name: quiet until hover, then link-colored. */
const LINKABLE_NAME_CLASS =
  "min-w-0 truncate text-left text-sm text-surface-900 transition-colors duration-75 hover:text-primary-700 hover:underline";
const UNLINK_ICON_CLASS =
  "inline-flex shrink-0 items-center rounded-md p-0.5 leading-none text-surface-400 transition-colors duration-75 hover:bg-error-50 hover:text-error-700";
const COPIED_FEEDBACK_MS = 1800;

async function copyTextToClipboard(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

/** Icon-only control with a hover explanation (and matching aria-label). */
function IconActionButton({
  label,
  onClick,
  className,
  children,
}: {
  label: string;
  onClick?: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <HoverPopover content={label}>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className={className}
        aria-label={label}
        onClick={onClick}
      >
        {children}
      </Button>
    </HoverPopover>
  );
}

export type DeviceTableRow = Device;

export interface DevicesTableLabels {
  colDevice: string;
  colSerial: string;
  colGoogleAccount: string;
  colFreekioskUnlock: string;
  colReader: string;
  colHouse?: string;
  colLastTelemetry: string;
  colLastInteraction: string;
  colInstalledOn: string;
  colAppVersion: string;
  colInUse: string;
  colActions: string;
  emptyDefault: string;
  readerPair: string;
  readerOpenTitle: string;
  readerUnpairTitle: string;
  assign?: string;
  houseRemoveTitle?: string;
  houseOpenTitle?: string;
  insertInstalledOn: string;
  lastTelemetryNever: string;
  valueAppVersionUnknown: string;
  valueAppVersionLegacy: string;
  valueFreekioskUnlockUnavailable: string;
  moreActionsTitle: string;
  menuRegenerateCredentials: string;
  menuRevokeDevice: string;
  menuReactivateDevice: string;
  menuManagePresences?: string;
  menuOtherInfo: string;
  menuNotes: string;
  unassignDeviceTitle?: string;
  changeRoomTitle?: string;
  /** Assign a room when the tablet has none (requires a structure). */
  addRoomTitle?: string;
  /** Shown when the tablet has no room label. */
  noRoomLabel: string;
  editGoogleAccountTitle?: string;
  editNotesTitle: string;
  editInstalledOnTitle: string;
  notesPlaceholder: string;
  telemetryBattery: string;
  telemetryWifi: string;
  telemetryAudio: string;
  telemetryStorage: string;
  telemetryMemory: string;
  telemetryUnavailable: string;
  telemetryDataAge: (age: string) => string;
}

export interface DevicesTableProps {
  devices: DeviceTableRow[];
  labels: DevicesTableLabels;
  /** Persist column prefs / page size. */
  preferencesKey: string;
  showHouseColumn?: boolean;
  /** Extra columns that start hidden in this context (still toggleable in preferences). */
  defaultHiddenColumns?: readonly DeviceTableSortColumn[];
  emptyMessage?: string;
  selectedIds?: ReadonlySet<string>;
  onSelectedIdsChange?: (next: Set<string>) => void;
  selectionBulkAction?: DataTableSelectionBulkAction;
  /** Heading on the left of the table's header row. */
  title?: ReactNode;
  /** Controls on the right of the header row (search, primary action…). */
  actions?: ReactNode;
  toolbar?: ReactNode;
  loadState?: DataTableLoadState;
  resetToken?: string | number;
  /** When set, rows can be drag-reordered (house device lists). */
  onReorder?: (reordered: DeviceTableRow[]) => void;
  reorderDisabled?: boolean;
  dragHandleLabel?: string;
  onPairReader: (device: DeviceTableRow) => void;
  onUnpairReader: (device: DeviceTableRow) => void;
  onAssignHouse?: (device: DeviceTableRow) => void;
  onRemoveHouse?: (device: DeviceTableRow) => void;
  onOpenHouse?: (houseId: string) => void;
  onUnassignDevice?: (device: DeviceTableRow) => void;
  onRegenerateCredentials: (device: DeviceTableRow) => void;
  onToggleActive: (device: DeviceTableRow) => void;
  onToggleShelved: (device: DeviceTableRow) => void;
  onShowOtherInfo: (device: DeviceTableRow) => void;
  onManagePresences?: (device: DeviceTableRow) => void;
  /** Opens the explicit “change room” flow (not an inline rename). */
  onChangeRoom: (device: DeviceTableRow) => void;
  onUpdateGoogleAccount: (
    device: DeviceTableRow,
    googleAccount: string,
  ) => Promise<void>;
  /** Both reject after showing their own error toast. */
  onUpdateNotes: (device: DeviceTableRow, notes: string | null) => Promise<void>;
  /** `installedOn` is YYYY-MM-DD or null to clear; rejects after its own error toast. */
  onUpdateInstalledOn: (
    device: DeviceTableRow,
    installedOn: string | null,
  ) => Promise<void>;
}

type TelemetryMetric = {
  label: string;
  value: string;
  icon: LucideIcon;
  colorClassName?: string;
};

const TELEMETRY_AMBER_CLASS = "text-warning-600";
const TELEMETRY_GREEN_CLASS = "text-success-600";
const TELEMETRY_RED_CLASS = "text-error-600";

function getWifiIcon(signalLevel: number | null): LucideIcon {
  if (signalLevel === null || signalLevel > 70) {
    return Wifi;
  }
  if (signalLevel > 40) {
    return WifiHigh;
  }
  return WifiLow;
}

function getAudioIcon(volume: number | null): LucideIcon {
  if (volume === null || volume > 70) {
    return Volume2;
  }
  if (volume > 40) {
    return Volume1;
  }
  return Volume;
}

function getUsageColorClass(usedPercent: number | null): string | undefined {
  if (usedPercent !== null && usedPercent > 90) {
    return TELEMETRY_RED_CLASS;
  }
  if (usedPercent !== null && usedPercent > 70) {
    return TELEMETRY_AMBER_CLASS;
  }
  return undefined;
}

function isStaleTelemetryTimestamp(timestamp: Date): boolean {
  return Date.now() - timestamp.getTime() > TELEMETRY_FRESH_MS;
}

function formatPercentValue(value: number | null, unavailableLabel: string) {
  return value === null ? unavailableLabel : `${Math.round(value)}%`;
}

function hasDisplayableTelemetry(
  payload: DeviceStatusPayload | null | undefined,
): boolean {
  const statusData = getDeviceStatusData(payload);
  return (
    getDeviceStatusTimestamp(payload) !== null ||
    readTelemetryNumber(statusData, "battery", "level") !== null ||
    readTelemetryBoolean(statusData, "battery", "charging") !== null ||
    readTelemetryNumber(statusData, "wifi", "signalLevel") !== null ||
    readTelemetryNumber(statusData, "audio", "volume") !== null ||
    readTelemetryNumber(statusData, "storage", "usedPercent") !== null ||
    readTelemetryNumber(statusData, "memory", "usedPercent") !== null
  );
}

function getTelemetryMetrics(
  device: DeviceTableRow,
  labels: DevicesTableLabels,
): TelemetryMetric[] {
  const statusData = getDeviceStatusData(device.device_status_payload);
  const batteryLevel = readTelemetryNumber(statusData, "battery", "level");
  const batteryCharging = readTelemetryBoolean(
    statusData,
    "battery",
    "charging",
  );
  const wifiSignalLevel = readTelemetryNumber(
    statusData,
    "wifi",
    "signalLevel",
  );
  const audioVolume = readTelemetryNumber(statusData, "audio", "volume");
  const storageUsedPercent = readTelemetryNumber(
    statusData,
    "storage",
    "usedPercent",
  );
  const memoryUsedPercent = readTelemetryNumber(
    statusData,
    "memory",
    "usedPercent",
  );

  return [
    {
      label: labels.telemetryBattery,
      value: formatPercentValue(batteryLevel, labels.telemetryUnavailable),
      icon: getBatteryIcon(batteryLevel, batteryCharging),
      colorClassName:
        batteryCharging === true
          ? TELEMETRY_GREEN_CLASS
          : batteryLevel !== null && batteryLevel < 15
            ? TELEMETRY_RED_CLASS
            : undefined,
    },
    {
      label: labels.telemetryWifi,
      value: formatPercentValue(wifiSignalLevel, labels.telemetryUnavailable),
      icon: getWifiIcon(wifiSignalLevel),
      colorClassName:
        wifiSignalLevel !== null && wifiSignalLevel < 10
          ? TELEMETRY_RED_CLASS
          : undefined,
    },
    {
      label: labels.telemetryAudio,
      value: formatPercentValue(audioVolume, labels.telemetryUnavailable),
      icon: getAudioIcon(audioVolume),
      colorClassName:
        audioVolume !== null && audioVolume < 5
          ? TELEMETRY_AMBER_CLASS
          : undefined,
    },
    {
      label: labels.telemetryStorage,
      value: formatPercentValue(
        storageUsedPercent,
        labels.telemetryUnavailable,
      ),
      icon: HardDrive,
      colorClassName: getUsageColorClass(storageUsedPercent),
    },
    {
      label: labels.telemetryMemory,
      value: formatPercentValue(memoryUsedPercent, labels.telemetryUnavailable),
      icon: MemoryStick,
      colorClassName: getUsageColorClass(memoryUsedPercent),
    },
  ];
}

function DeviceTelemetrySummary({
  device,
  labels,
}: {
  device: DeviceTableRow;
  labels: DevicesTableLabels;
}) {
  const metrics = getTelemetryMetrics(device, labels);
  const telemetryTimestamp = getDeviceStatusTimestamp(
    device.device_status_payload,
  );
  const isTelemetryTimestampStale =
    telemetryTimestamp !== null &&
    isStaleTelemetryTimestamp(telemetryTimestamp);

  return (
    <div className="space-y-2">
      <div className="grid max-w-[calc(100vw-2rem)] grid-cols-[max-content_max-content] gap-x-3 gap-y-2">
        {metrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <div
              key={metric.label}
              title={metric.label}
              className={cn(
                "flex min-w-0 items-center gap-1.5 text-sm font-semibold leading-none",
                metric.colorClassName ?? "text-surface-900",
              )}
            >
              <Icon
                className={cn(
                  "h-4 w-4 shrink-0",
                  metric.colorClassName ? "text-current" : "text-primary-600",
                )}
              />
              <span className="break-words">{metric.value}</span>
            </div>
          );
        })}
      </div>
      {telemetryTimestamp && (
        <p
          className={cn(
            "text-[11px] leading-none",
            isTelemetryTimestampStale ? "text-error-600" : "text-surface-500",
          )}
        >
          {labels.telemetryDataAge(formatRelativeTime(telemetryTimestamp))}
        </p>
      )}
    </div>
  );
}

type DeviceCellHandlers = {
  labels: DevicesTableLabels;
  onPairReader: (device: DeviceTableRow) => void;
  onUnpairReader: (device: DeviceTableRow) => void;
  onAssignHouse?: (device: DeviceTableRow) => void;
  onRemoveHouse?: (device: DeviceTableRow) => void;
  onOpenHouse?: (houseId: string) => void;
  onUnassignDevice?: (device: DeviceTableRow) => void;
  onRegenerateCredentials: (device: DeviceTableRow) => void;
  onToggleActive: (device: DeviceTableRow) => void;
  onToggleShelved: (device: DeviceTableRow) => void;
  onShowOtherInfo: (device: DeviceTableRow) => void;
  onManagePresences?: (device: DeviceTableRow) => void;
  onChangeRoom: (device: DeviceTableRow) => void;
  onUpdateGoogleAccount: (
    device: DeviceTableRow,
    googleAccount: string,
  ) => Promise<void>;
  handleEditGoogleAccount: (device: DeviceTableRow) => void;
  handleEditNotes: (device: DeviceTableRow) => void;
  handleEditInstalledOn: (device: DeviceTableRow) => void;
  handleToggleActive: (device: DeviceTableRow) => void;
};

function renderSerialCell(device: DeviceTableRow, h: DeviceCellHandlers) {
  const hasTelemetry = hasDisplayableTelemetry(device.device_status_payload);
  const notes = device.notes?.trim() ?? "";

  const serialControl = (
    <CopyableSerialNumber
      value={device.serial_number}
      hoverHint={!hasTelemetry}
      className={
        hasTelemetry
          ? "underline decoration-surface-300 decoration-dotted underline-offset-4"
          : undefined
      }
    />
  );

  return (
    <div id={`device-row-${device.id}`} className="group/cell min-w-0">
      <div className="flex min-w-0 items-center gap-2">
        <DeviceTelemetryDot device={device} />
        <div className="min-w-0">
          <div className="flex items-center gap-1">
            {hasTelemetry ? (
              <HoverPopover
                content={
                  <DeviceTelemetrySummary device={device} labels={h.labels} />
                }
                placement="below"
                contentClassName="p-2.5"
              >
                <span className="inline-flex shrink-0">{serialControl}</span>
              </HoverPopover>
            ) : (
              <span className="inline-flex shrink-0">{serialControl}</span>
            )}
            <span className="inline-flex shrink-0 items-center">
              <DeviceInUseWarning device={device} />
              <DeviceAlertIcons device={device} />
              <DeviceSetupBadge device={device} />
            </span>
          </div>
          {notes ? (
            <p
              className="mt-0.5 min-w-0 truncate text-xs text-surface-500"
              title={notes}
            >
              {notes}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function renderDeviceCell(device: DeviceTableRow, h: DeviceCellHandlers) {
  const roomName = device.room_name?.trim() || null;
  const hasHouse = device.house_id != null;
  const roomLabel = roomName ?? h.labels.noRoomLabel;
  const canEditRoom = hasHouse;
  const roomActionLabel =
    (roomName ? h.labels.changeRoomTitle : h.labels.addRoomTitle) ??
    h.labels.changeRoomTitle ??
    h.labels.addRoomTitle ??
    "";

  return (
    <div className="group/cell min-w-0">
      <div className="flex min-w-0 items-center gap-0.5">
        <span
          className={cn(
            "min-w-0 truncate text-sm font-medium",
            roomName ? "text-surface-900" : "text-surface-400",
          )}
          title={roomLabel}
        >
          {roomLabel}
        </span>
        {canEditRoom && roomActionLabel ? (
          <IconActionButton
            label={roomActionLabel}
            className="shrink-0"
            onClick={() => h.onChangeRoom(device)}
          >
            {roomName ? (
              <ArrowLeftRight className="h-3.5 w-3.5" />
            ) : (
              <SquarePlus className="h-3.5 w-3.5" />
            )}
          </IconActionButton>
        ) : null}
      </div>
    </div>
  );
}

function renderGoogleAccountCell(
  device: DeviceTableRow,
  h: DeviceCellHandlers,
) {
  return (
    <div className="group/cell flex min-w-0 items-center gap-0.5">
      <ClickToCopyId
        value={device.google_account}
        variant="plain"
        className="min-w-0"
      />
      <IconActionButton
        label={h.labels.editGoogleAccountTitle ?? h.labels.colGoogleAccount}
        className={HOVER_REVEAL_CLASS}
        onClick={() => h.handleEditGoogleAccount(device)}
      >
        <PencilLine className="h-3.5 w-3.5" />
      </IconActionButton>
    </div>
  );
}

function renderInstalledOnCell(
  device: DeviceTableRow,
  h: DeviceCellHandlers,
) {
  if (!device.installed_on) {
    return (
      <div className="group/cell min-w-0">
        <span className="text-surface-400 group-hover/cell:hidden group-focus-within/cell:hidden [@media(hover:none)]:hidden">
          —
        </span>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          leftIcon={<Clock className="h-3.5 w-3.5" />}
          className="-ml-2 hidden text-primary-700 hover:bg-primary-50 hover:text-primary-800 group-hover/cell:inline-flex group-focus-within/cell:inline-flex [@media(hover:none)]:inline-flex"
          onClick={() => h.handleEditInstalledOn(device)}
        >
          {h.labels.insertInstalledOn}
        </Button>
      </div>
    );
  }

  return (
    <div className="group/cell flex min-w-0 items-center gap-0.5">
      <span className={CELL_TRUNCATE_CLASS}>
        {/* Date-only value: format in UTC so it never shifts by a day. */}
        {formatDate(device.installed_on, { timeZone: "UTC" })}
      </span>
      <IconActionButton
        label={h.labels.editInstalledOnTitle}
        className={HOVER_REVEAL_CLASS}
        onClick={() => h.handleEditInstalledOn(device)}
      >
        <PencilLine className="h-3.5 w-3.5" />
      </IconActionButton>
    </div>
  );
}

function ReaderSerialControl({
  serialNumber,
  readerId,
  openLabel,
}: {
  serialNumber: string;
  readerId: string;
  openLabel: string;
}) {
  const { t } = useTranslation("common");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setCopied(false);
    }, COPIED_FEEDBACK_MS);
    return () => window.clearTimeout(timeoutId);
  }, [copied]);

  const displaySerial = readerSerialSuffix(serialNumber);
  const serialTruncated =
    displaySerial !== normalizeReaderSerialNumber(serialNumber);

  const handleCopy = async () => {
    if (copied) {
      return;
    }
    const ok = await copyTextToClipboard(serialNumber);
    if (ok) {
      setCopied(true);
      return;
    }
    toast.error(t("copyFailed"));
  };

  const readerHref = `/readers?readerId=${readerId}`;

  return (
    <HoverPopover
      interactive
      contentClassName="gap-0 overflow-hidden p-0"
      content={
        <div className="relative z-[1] flex items-stretch divide-x divide-surface-200">
          <button
            type="button"
            onClick={() => void handleCopy()}
            className={cn(
              "inline-flex items-center gap-1.5 px-2.5 py-1.5 transition-colors duration-75 hover:bg-surface-100",
              copied && "text-success-600",
            )}
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
            ) : (
              <Copy className="h-3.5 w-3.5 shrink-0 text-surface-500" aria-hidden />
            )}
            {copied
              ? t("copied")
              : serialTruncated
                ? t("copySerial")
                : t("copy")}
          </button>
          <Link
            to={readerHref}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 transition-colors duration-75 hover:bg-surface-100"
          >
            {openLabel}
            <ArrowUpRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
          </Link>
        </div>
      }
    >
      <Link
        to={readerHref}
        className={cn(
          LINKABLE_NAME_CLASS,
          "font-mono text-[13px] tabular-nums",
        )}
      >
        {displaySerial}
      </Link>
    </HoverPopover>
  );
}

function renderUnlockCell(device: DeviceTableRow, h: DeviceCellHandlers) {
  return device.freekiosk_unlock_code ? (
    <ClickToCopyId
      value={device.freekiosk_unlock_code}
      variant="plain"
      className="font-mono text-[13px] tabular-nums"
    />
  ) : (
    <span className="text-surface-400">
      {h.labels.valueFreekioskUnlockUnavailable}
    </span>
  );
}

function renderReaderCell(device: DeviceTableRow, h: DeviceCellHandlers) {
  const reader = device.reader;
  if (!reader || !isReaderPaired(reader)) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="xs"
        className="-ml-2 text-primary-700 hover:bg-primary-50 hover:text-primary-800"
        leftIcon={<Link2 className="h-3.5 w-3.5" />}
        onClick={() => h.onPairReader(device)}
      >
        {h.labels.readerPair}
      </Button>
    );
  }

  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
      <ReaderSerialControl
        serialNumber={reader.serial_number}
        readerId={reader.id}
        openLabel={h.labels.readerOpenTitle}
      />
      <HoverPopover content={h.labels.readerUnpairTitle}>
        <button
          type="button"
          onClick={() => h.onUnpairReader(device)}
          aria-label={`${h.labels.readerUnpairTitle} (${reader.serial_number})`}
          className={UNLINK_ICON_CLASS}
        >
          <Link2Off className="h-4 w-4" />
        </button>
      </HoverPopover>
      <ReaderLiveStatusDot reader={reader} />
    </span>
  );
}

function renderHouseCell(device: DeviceTableRow, h: DeviceCellHandlers) {
  const house = device.house;
  if (house) {
    return (
      <div className="flex min-w-0 items-center gap-1.5">
        <HoverPopover
          content={
            h.labels.houseOpenTitle ? (
              <span className="inline-flex items-center gap-1.5">
                {h.labels.houseOpenTitle}
                <ArrowUpRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
              </span>
            ) : (
              house.name
            )
          }
        >
          <button
            type="button"
            onClick={() => h.onOpenHouse?.(house.id)}
            className={LINKABLE_NAME_CLASS}
          >
            {house.name}
          </button>
        </HoverPopover>
        {h.labels.houseRemoveTitle ? (
          <HoverPopover content={h.labels.houseRemoveTitle}>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                h.onRemoveHouse?.(device);
              }}
              aria-label={h.labels.houseRemoveTitle}
              className={UNLINK_ICON_CLASS}
            >
              <Link2Off className="h-4 w-4" />
            </button>
          </HoverPopover>
        ) : null}
      </div>
    );
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="xs"
      className="-ml-2 text-primary-700 hover:bg-primary-50 hover:text-primary-800"
      leftIcon={<Plus className="h-3.5 w-3.5" />}
      onClick={() => h.onAssignHouse?.(device)}
    >
      {h.labels.assign}
    </Button>
  );
}

function renderAppVersionCell(device: DeviceTableRow, h: DeviceCellHandlers) {
  const appVersion = formatDeviceAppVersion(device, {
    unknown: h.labels.valueAppVersionUnknown,
    legacy: h.labels.valueAppVersionLegacy,
  });
  const isUnknown = appVersion === h.labels.valueAppVersionUnknown;
  return (
    <span
      className={cn(
        CELL_TRUNCATE_CLASS,
        isUnknown ? "text-surface-400" : "font-mono text-[13px] text-surface-700",
      )}
    >
      {appVersion}
    </span>
  );
}

function renderRelativeTimestampCell(
  iso: string | null | undefined,
  neverLabel: string,
) {
  if (iso) {
    return (
      <HoverPopover content={formatDateTime(iso)} placement="above">
        <span className="cursor-default whitespace-nowrap tabular-nums">
          {formatRelativeTime(iso)}
        </span>
      </HoverPopover>
    );
  }
  return (
    <span className="whitespace-nowrap text-surface-400">{neverLabel}</span>
  );
}

function renderTelemetryCell(device: DeviceTableRow, h: DeviceCellHandlers) {
  return renderRelativeTimestampCell(
    device.last_device_report_at,
    h.labels.lastTelemetryNever,
  );
}

function renderInteractionCell(device: DeviceTableRow, h: DeviceCellHandlers) {
  return renderRelativeTimestampCell(
    device.last_interaction_at,
    h.labels.lastTelemetryNever,
  );
}

function renderInUseCell(device: DeviceTableRow, h: DeviceCellHandlers) {
  return (
    <Switch
      checked={device.is_shelved !== true}
      onChange={() => h.onToggleShelved(device)}
      aria-label={h.labels.colInUse}
    />
  );
}

function renderActionsCell(device: DeviceTableRow, h: DeviceCellHandlers) {
  return (
    <div className="relative flex items-center justify-center gap-1">
      {h.onUnassignDevice && h.labels.unassignDeviceTitle ? (
        <IconActionButton
          label={h.labels.unassignDeviceTitle}
          className="text-error-600 hover:bg-error-50 hover:text-error-700 dark:hover:text-error-800"
          onClick={() => h.onUnassignDevice?.(device)}
        >
          <Link2Off className="h-4 w-4" />
        </IconActionButton>
      ) : null}
      <ActionMenu
        renderTrigger={(triggerProps) => (
          <HoverPopover content={h.labels.moreActionsTitle}>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={h.labels.moreActionsTitle}
              {...triggerProps}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </HoverPopover>
        )}
      >
        <ActionMenuItem
          icon={KeyRound}
          onSelect={() => h.onRegenerateCredentials(device)}
        >
          {h.labels.menuRegenerateCredentials}
        </ActionMenuItem>
        <ActionMenuItem
          icon={device.is_revoked ? CheckCircle2 : Ban}
          onSelect={() => h.handleToggleActive(device)}
        >
          {device.is_revoked
            ? h.labels.menuReactivateDevice
            : h.labels.menuRevokeDevice}
        </ActionMenuItem>
        {h.onManagePresences &&
        h.labels.menuManagePresences &&
        device.house_id ? (
          <ActionMenuItem
            icon={Users}
            onSelect={() => h.onManagePresences?.(device)}
          >
            {h.labels.menuManagePresences}
          </ActionMenuItem>
        ) : null}
        <ActionMenuItem
          icon={StickyNote}
          onSelect={() => h.handleEditNotes(device)}
        >
          {h.labels.menuNotes}
        </ActionMenuItem>
        <ActionMenuItem
          icon={Info}
          onSelect={() => h.onShowOtherInfo(device)}
        >
          {h.labels.menuOtherInfo}
        </ActionMenuItem>
      </ActionMenu>
    </div>
  );
}

export function DevicesTable({
  devices,
  labels,
  preferencesKey,
  showHouseColumn = false,
  defaultHiddenColumns,
  emptyMessage,
  selectedIds,
  onSelectedIdsChange,
  selectionBulkAction,
  title,
  actions,
  toolbar,
  loadState,
  resetToken,
  onReorder,
  reorderDisabled = false,
  dragHandleLabel,
  onPairReader,
  onUnpairReader,
  onAssignHouse,
  onRemoveHouse,
  onOpenHouse,
  onUnassignDevice,
  onRegenerateCredentials,
  onToggleActive,
  onToggleShelved,
  onShowOtherInfo,
  onManagePresences,
  onChangeRoom,
  onUpdateGoogleAccount,
  onUpdateNotes,
  onUpdateInstalledOn,
}: DevicesTableProps) {
  const { t } = useTranslation("common");
  const { t: tDevices } = useTranslation("devices");
  const tableLabels = buildDataTableLabels(t);
  const [editingGoogleAccountDevice, setEditingGoogleAccountDevice] =
    useState<DeviceTableRow | null>(null);
  const [googleAccountDraft, setGoogleAccountDraft] = useState("");
  const [savingGoogleAccount, setSavingGoogleAccount] = useState(false);
  const [editingNotesDevice, setEditingNotesDevice] =
    useState<DeviceTableRow | null>(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [editingInstalledOnDevice, setEditingInstalledOnDevice] =
    useState<DeviceTableRow | null>(null);
  const [installedOnDraft, setInstalledOnDraft] = useState("");
  const [savingInstalledOn, setSavingInstalledOn] = useState(false);
  const [revokingDevice, setRevokingDevice] = useState<DeviceTableRow | null>(
    null,
  );

  const handleEditGoogleAccount = (device: DeviceTableRow) => {
    setEditingGoogleAccountDevice(device);
    setGoogleAccountDraft(device.google_account);
  };

  /** Revoking blocks the tablet login, so it asks for confirmation; reactivating does not. */
  const handleToggleActive = (device: DeviceTableRow) => {
    if (device.is_revoked) {
      onToggleActive(device);
      return;
    }
    setRevokingDevice(device);
  };

  const handleConfirmRevoke = () => {
    if (!revokingDevice) return;
    onToggleActive(revokingDevice);
    setRevokingDevice(null);
  };

  const handleEditNotes = (device: DeviceTableRow) => {
    setEditingNotesDevice(device);
    setNotesDraft(device.notes ?? "");
  };

  const handleCloseNotesModal = () => {
    if (savingNotes) return;
    setEditingNotesDevice(null);
    setNotesDraft("");
  };

  const handleSaveNotes = async () => {
    if (!editingNotesDevice) return;
    const nextNotes = notesDraft.trim() || null;
    const currentNotes = editingNotesDevice.notes?.trim() || null;
    if (nextNotes === currentNotes) {
      handleCloseNotesModal();
      return;
    }
    try {
      setSavingNotes(true);
      await onUpdateNotes(editingNotesDevice, nextNotes);
      setEditingNotesDevice(null);
      setNotesDraft("");
    } catch {
      /* the caller already toasted the error; keep the modal open for a retry */
    } finally {
      setSavingNotes(false);
    }
  };

  const handleEditInstalledOn = (device: DeviceTableRow) => {
    setEditingInstalledOnDevice(device);
    setInstalledOnDraft(device.installed_on ?? todayDateInputValue());
  };

  const handleCloseInstalledOnModal = () => {
    if (savingInstalledOn) return;
    setEditingInstalledOnDevice(null);
    setInstalledOnDraft("");
  };

  const handleSaveInstalledOn = async () => {
    if (!editingInstalledOnDevice) return;
    const nextInstalledOn = installedOnDraft || null;
    if (nextInstalledOn === editingInstalledOnDevice.installed_on) {
      handleCloseInstalledOnModal();
      return;
    }
    try {
      setSavingInstalledOn(true);
      await onUpdateInstalledOn(editingInstalledOnDevice, nextInstalledOn);
      setEditingInstalledOnDevice(null);
      setInstalledOnDraft("");
    } catch {
      /* the caller already toasted the error; keep the modal open for a retry */
    } finally {
      setSavingInstalledOn(false);
    }
  };

  const handleCloseGoogleAccountModal = () => {
    if (savingGoogleAccount) return;
    setEditingGoogleAccountDevice(null);
    setGoogleAccountDraft("");
  };

  const handleSaveGoogleAccount = async () => {
    if (!editingGoogleAccountDevice) return;
    const trimmed = googleAccountDraft.trim();
    if (!trimmed || trimmed === editingGoogleAccountDevice.google_account) {
      handleCloseGoogleAccountModal();
      return;
    }
    try {
      setSavingGoogleAccount(true);
      await onUpdateGoogleAccount(editingGoogleAccountDevice, trimmed);
      setEditingGoogleAccountDevice(null);
      setGoogleAccountDraft("");
    } catch {
      /* the caller already toasted the error; keep the modal open for a retry */
    } finally {
      setSavingGoogleAccount(false);
    }
  };

  const cellHandlers: DeviceCellHandlers = {
    labels,
    onPairReader,
    onUnpairReader,
    onAssignHouse,
    onRemoveHouse,
    onOpenHouse,
    onUnassignDevice,
    onRegenerateCredentials,
    onToggleActive,
    onToggleShelved,
    onShowOtherInfo,
    onManagePresences,
    onChangeRoom,
    onUpdateGoogleAccount,
    handleEditGoogleAccount,
    handleEditNotes,
    handleEditInstalledOn,
    handleToggleActive,
  };
  const cellHandlersRef = useRef(cellHandlers);
  cellHandlersRef.current = cellHandlers;

  const columns = useMemo((): DataTableColumn<
    DeviceTableRow,
    DeviceTableSortColumn
  >[] => {
    const withHandlers =
      (render: (device: DeviceTableRow, h: DeviceCellHandlers) => ReactNode) =>
      (device: DeviceTableRow) =>
        render(device, cellHandlersRef.current);

    const cols: DataTableColumn<DeviceTableRow, DeviceTableSortColumn>[] = [
      {
        id: "serial",
        header: labels.colSerial,
        sortKey: "serial",
        sortKind: "text",
        getSortValue: (device) => device.serial_number,
        // Room for telemetry + serial suffix + in-use/revoked icons.
        defaultWidth: 200,
        minWidth: 180,
        cell: withHandlers(renderSerialCell),
      },
    ];

    if (showHouseColumn && labels.colHouse) {
      cols.push({
        id: "house",
        header: labels.colHouse,
        sortKey: "house",
        sortKind: "text",
        getSortValue: (device) => device.house?.name,
        defaultWidth: 160,
        cell: withHandlers(renderHouseCell),
      });
    }

    cols.push(
      {
        id: "device",
        header: labels.colDevice,
        sortKey: "device",
        sortKind: "text",
        getSortValue: (device) => device.room_name ?? "",
        defaultWidth: 320,
        cell: withHandlers(renderDeviceCell),
      },
      {
        id: "reader",
        header: labels.colReader,
        sortKey: "reader",
        sortKind: "text",
        getSortValue: (device) => device.reader?.serial_number,
        defaultWidth: 160,
        cell: withHandlers(renderReaderCell),
      },
      {
        id: "googleAccount",
        defaultHidden: true,
        header: labels.colGoogleAccount,
        sortKey: "googleAccount",
        sortKind: "text",
        getSortValue: (device) => device.google_account,
        defaultWidth: 180,
        cell: withHandlers(renderGoogleAccountCell),
      },
      {
        id: "unlock",
        defaultHidden: true,
        header: labels.colFreekioskUnlock,
        sortKey: "unlock",
        sortKind: "text",
        getSortValue: (device) => device.freekiosk_unlock_code,
        defaultWidth: 140,
        cell: withHandlers(renderUnlockCell),
      },
    );

    cols.push(
      {
        id: "appVersion",
        header: labels.colAppVersion,
        sortKey: "appVersion",
        sortKind: "number",
        getSortValue: (device) =>
          deviceAppVersionSortValue(device, {
            unknown: labels.valueAppVersionUnknown,
            legacy: labels.valueAppVersionLegacy,
          }),
        defaultWidth: 120,
        cell: withHandlers(renderAppVersionCell),
      },
      {
        id: "telemetry",
        header: labels.colLastTelemetry,
        sortKey: "telemetry",
        sortKind: "date",
        getSortValue: (device) => device.last_device_report_at ?? null,
        defaultWidth: 140,
        cell: withHandlers(renderTelemetryCell),
      },
      {
        id: "interaction",
        header: labels.colLastInteraction,
        sortKey: "interaction",
        sortKind: "date",
        getSortValue: (device) => device.last_interaction_at ?? null,
        defaultWidth: 140,
        cell: withHandlers(renderInteractionCell),
      },
      {
        id: "installedOn",
        header: labels.colInstalledOn,
        sortKey: "installedOn",
        sortKind: "date",
        getSortValue: (device) => device.installed_on,
        defaultWidth: 140,
        cell: withHandlers(renderInstalledOnCell),
      },
      {
        id: "inUse",
        header: labels.colInUse,
        defaultWidth: 120,
        cell: withHandlers(renderInUseCell),
      },
      createActionsColumn(labels.colActions, {
        cellClassName: "px-3 text-center font-medium",
        cell: withHandlers(renderActionsCell),
      }),
    );

    if (!defaultHiddenColumns) {
      return cols;
    }
    return cols.map((column) =>
      column.sortKey && defaultHiddenColumns.includes(column.sortKey)
        ? { ...column, defaultHidden: true }
        : column,
    );
  }, [defaultHiddenColumns, labels, showHouseColumn]);

  return (
    <>
      <DataTable<DeviceTableRow, DeviceTableSortColumn>
        labels={tableLabels}
        closeLabel={t("close")}
        loadingLabel={t("loading")}
        rows={devices}
        columns={columns}
        getRowKey={(row) => row.id}
        defaultSortColumn="serial"
        preferencesKey={preferencesKey}
        paginate={!onReorder}
        resetToken={resetToken}
        loadState={loadState}
        emptyMessage={emptyMessage ?? labels.emptyDefault}
        selectedIds={selectedIds}
        onSelectedIdsChange={onSelectedIdsChange}
        selectionBulkAction={selectionBulkAction}
        selectionRowAriaLabel={(device) =>
          tDevices("page.selectRow", {
            name: device.room_name?.trim() || labels.noRoomLabel,
          })
        }
        title={title}
        actions={actions}
        toolbar={toolbar}
        reorder={
          onReorder && dragHandleLabel
            ? {
                onReorder,
                disabled: reorderDisabled,
                dragHandleLabel,
              }
            : undefined
        }
        rowClassName={(row) =>
          cn(
            // Match the snappy Actions-column hover (no long color fade).
            "hover:bg-surface-50 dark:hover:bg-surface-100/50",
            selectedIds?.has(row.id) && "bg-primary-50/40 dark:bg-primary-50/60",
          )
        }
      />

      <Modal
        isOpen={editingGoogleAccountDevice != null}
        onClose={handleCloseGoogleAccountModal}
        closeLabel={t("close")}
        title={labels.editGoogleAccountTitle ?? labels.colGoogleAccount}
        size="sm"
        footer={
          <ModalFooter>
            <Button
              variant="outline"
              onClick={handleCloseGoogleAccountModal}
              disabled={savingGoogleAccount}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={() => void handleSaveGoogleAccount()}
              isLoading={savingGoogleAccount}
              disabled={!googleAccountDraft.trim()}
            >
              {t("save")}
            </Button>
          </ModalFooter>
        }
      >
        <Input
          value={googleAccountDraft}
          onChange={(event) => setGoogleAccountDraft(event.target.value)}
          placeholder={labels.colGoogleAccount}
          autoFocus
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void handleSaveGoogleAccount();
            }
          }}
        />
      </Modal>

      <Modal
        isOpen={editingNotesDevice != null}
        onClose={handleCloseNotesModal}
        closeLabel={t("close")}
        title={labels.editNotesTitle}
        size="sm"
        footer={
          <ModalFooter>
            <Button
              variant="outline"
              onClick={handleCloseNotesModal}
              disabled={savingNotes}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={() => void handleSaveNotes()}
              isLoading={savingNotes}
            >
              {t("save")}
            </Button>
          </ModalFooter>
        }
      >
        <Textarea
          value={notesDraft}
          onChange={(event) => setNotesDraft(event.target.value)}
          placeholder={labels.notesPlaceholder}
          rows={4}
          autoFocus
        />
      </Modal>

      <Modal
        isOpen={editingInstalledOnDevice != null}
        onClose={handleCloseInstalledOnModal}
        closeLabel={t("close")}
        title={labels.editInstalledOnTitle}
        size="sm"
        footer={
          <ModalFooter>
            <Button
              variant="outline"
              onClick={handleCloseInstalledOnModal}
              disabled={savingInstalledOn}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={() => void handleSaveInstalledOn()}
              isLoading={savingInstalledOn}
            >
              {t("save")}
            </Button>
          </ModalFooter>
        }
      >
        <Input
          type="date"
          label={labels.colInstalledOn}
          value={installedOnDraft}
          onChange={(event) => setInstalledOnDraft(event.target.value)}
          autoFocus
        />
      </Modal>

      <ConfirmModal
        isOpen={revokingDevice != null}
        onClose={() => setRevokingDevice(null)}
        onConfirm={handleConfirmRevoke}
        closeLabel={t("close")}
        cancelText={t("cancel")}
        confirmText={labels.menuRevokeDevice}
        title={labels.menuRevokeDevice}
        message={tDevices("page.revokeConfirm", {
          name: revokingDevice?.room_name?.trim() || revokingDevice?.kiosk_code,
        })}
        isDestructive
      />
    </>
  );
}
